/*
 * Dialoguri touch reutilizabile — înlocuiesc prompt()/confirm() native,
 * inutilizabile pe tabletele din hală (audit UX inginer, 30 iul 2026).
 * API pe Promise: askText() întoarce textul sau null la anulare;
 * askConfirm() întoarce true/false. Un singur overlay, se curăță singur.
 */

export interface AskTextOpts {
    label?: string;          // explicație sub titlu
    placeholder?: string;
    value?: string;          // pre-completat
    required?: boolean;      // nu lasă OK cu câmpul gol
    multiline?: boolean;     // textarea în loc de input
    okText?: string;
    inputMode?: 'text' | 'numeric' | 'decimal';
    type?: 'text' | 'date';  // „date" = selectorul de zi al telefonului
}

function _buildOverlay(inner: string): HTMLElement {
    const overlay = document.createElement('div');
    overlay.className = 'proc-overlay';
    overlay.innerHTML =
        '<div class="proc-container" style="max-width:480px" role="dialog" aria-modal="true">' + inner + '</div>';
    document.body.appendChild(overlay);
    return overlay;
}

export function askText(title: string, opts: AskTextOpts = {}): Promise<string | null> {
    return new Promise(resolve => {
        const field = opts.multiline
            ? `<textarea class="dx-input" id="dlg-ask-input" rows="3" placeholder="${opts.placeholder || ''}" style="min-height:80px"></textarea>`
            : `<input class="dx-input" id="dlg-ask-input" type="${opts.type || 'text'}" inputmode="${opts.inputMode || 'text'}" placeholder="${opts.placeholder || ''}" style="min-height:44px">`;
        const overlay = _buildOverlay(
            '<div class="proc-header"><div class="proc-header-top">' +
            '<h2 class="proc-title" id="dlg-ask-title"></h2>' +
            '<button class="proc-close" id="dlg-ask-x">&times;</button></div></div>' +
            '<div style="padding:16px;display:flex;flex-direction:column;gap:12px">' +
            (opts.label ? '<div id="dlg-ask-label" style="color:var(--text-muted);font-size:.9rem;white-space:pre-line"></div>' : '') +
            field +
            '<div style="display:flex;gap:10px;justify-content:flex-end">' +
            '<button class="dx-btn-primary" id="dlg-ask-cancel" style="background:var(--card-bg);border:1px solid var(--border);color:var(--text-muted);padding:12px 22px;min-height:44px">Renunță</button>' +
            `<button class="dx-btn-primary" id="dlg-ask-ok" style="padding:12px 26px;min-height:44px">${opts.okText || 'Salvează'}</button>` +
            '</div></div>');
        // titlul și eticheta ca text (nu HTML) — pot conține date din DB
        overlay.querySelector('#dlg-ask-title')!.textContent = title;
        const lbl = overlay.querySelector('#dlg-ask-label');
        if (lbl && opts.label) lbl.textContent = opts.label;
        const input = overlay.querySelector('#dlg-ask-input') as HTMLInputElement | HTMLTextAreaElement;
        if (opts.value) input.value = opts.value;
        const okBtn = overlay.querySelector('#dlg-ask-ok') as HTMLButtonElement;

        const done = (val: string | null) => { overlay.remove(); document.removeEventListener('keydown', onKey); resolve(val); };
        const submit = () => {
            const v = input.value.trim();
            if (opts.required && !v) { input.focus(); input.style.borderColor = 'var(--red)'; return; }
            done(v);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') done(null);
            else if (e.key === 'Enter' && !opts.multiline) { e.preventDefault(); submit(); }
        };
        okBtn.addEventListener('click', submit);
        overlay.querySelector('#dlg-ask-cancel')!.addEventListener('click', () => done(null));
        overlay.querySelector('#dlg-ask-x')!.addEventListener('click', () => done(null));
        overlay.addEventListener('click', e => { if (e.target === overlay) done(null); });
        document.addEventListener('keydown', onKey);
        setTimeout(() => input.focus(), 50);
    });
}

export interface ChoiceOption { key: string; label: string; danger?: boolean }

/** Alegere din 2-4 variante, butoane mari pe verticală. null = închis fără alegere. */
export function askChoice(title: string, body: string, options: ChoiceOption[]): Promise<string | null> {
    return new Promise(resolve => {
        const btns = options.map(o =>
            `<button class="dx-btn-primary dlg-choice-btn" data-key="${o.key}" style="width:100%;min-height:48px;padding:12px 16px;text-align:left${o.danger ? ';background:rgba(239,83,80,.15);border:1px solid rgba(239,83,80,.5);color:#ef5350' : ''}"></button>`
        ).join('');
        const overlay = _buildOverlay(
            '<div class="proc-header"><div class="proc-header-top">' +
            '<h2 class="proc-title" id="dlg-ch-title"></h2>' +
            '<button class="proc-close" id="dlg-ch-x">&times;</button></div></div>' +
            '<div style="padding:16px;display:flex;flex-direction:column;gap:10px">' +
            (body ? '<div id="dlg-ch-body" style="color:var(--text-muted);font-size:.92rem;white-space:pre-line"></div>' : '') +
            btns + '</div>');
        overlay.querySelector('#dlg-ch-title')!.textContent = title;
        const b = overlay.querySelector('#dlg-ch-body');
        if (b && body) b.textContent = body;
        overlay.querySelectorAll('.dlg-choice-btn').forEach((btn, i) => {
            btn.textContent = options[i].label;
            btn.addEventListener('click', () => done(options[i].key));
        });
        const done = (v: string | null) => { overlay.remove(); document.removeEventListener('keydown', onKey); resolve(v); };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') done(null); };
        overlay.querySelector('#dlg-ch-x')!.addEventListener('click', () => done(null));
        overlay.addEventListener('click', e => { if (e.target === overlay) done(null); });
        document.addEventListener('keydown', onKey);
    });
}

export function askConfirm(title: string, body = '', okText = 'Da'): Promise<boolean> {
    return new Promise(resolve => {
        const overlay = _buildOverlay(
            '<div class="proc-header"><div class="proc-header-top">' +
            '<h2 class="proc-title" id="dlg-cf-title"></h2>' +
            '<button class="proc-close" id="dlg-cf-x">&times;</button></div></div>' +
            '<div style="padding:16px;display:flex;flex-direction:column;gap:14px">' +
            (body ? '<div id="dlg-cf-body" style="color:var(--text-muted);font-size:.92rem;white-space:pre-line"></div>' : '') +
            '<div style="display:flex;gap:10px;justify-content:flex-end">' +
            '<button class="dx-btn-primary" id="dlg-cf-cancel" style="background:var(--card-bg);border:1px solid var(--border);color:var(--text-muted);padding:12px 22px;min-height:44px">Nu</button>' +
            `<button class="dx-btn-primary" id="dlg-cf-ok" style="padding:12px 26px;min-height:44px">${okText}</button>` +
            '</div></div>');
        overlay.querySelector('#dlg-cf-title')!.textContent = title;
        const b = overlay.querySelector('#dlg-cf-body');
        if (b && body) b.textContent = body;
        const done = (v: boolean) => { overlay.remove(); document.removeEventListener('keydown', onKey); resolve(v); };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') done(false); };
        overlay.querySelector('#dlg-cf-ok')!.addEventListener('click', () => done(true));
        overlay.querySelector('#dlg-cf-cancel')!.addEventListener('click', () => done(false));
        overlay.querySelector('#dlg-cf-x')!.addEventListener('click', () => done(false));
        overlay.addEventListener('click', e => { if (e.target === overlay) done(false); });
        document.addEventListener('keydown', onKey);
    });
}

/** Confirmare cu bifă NEBIFATĂ (26 oct 2026, Dan: „când dai «Am făcut» direct, să te întrebe dacă ești sigur, iar
 *  confirmarea să nu fie direct bifată”). Butonul de confirmare se activează abia după bifă. true = confirmat. */
export function askConfirmCuBifa(title: string, body: string, bifa: string, okText = 'Confirm'): Promise<boolean> {
    return new Promise(resolve => {
        const overlay = _buildOverlay(
            '<div class="proc-header"><div class="proc-header-top">' +
            '<h2 class="proc-title" id="dlg-cb-title"></h2>' +
            '<button class="proc-close" id="dlg-cb-x" aria-label="Închide">&times;</button></div></div>' +
            '<div style="padding:16px;display:flex;flex-direction:column;gap:14px">' +
            '<div id="dlg-cb-body" style="color:var(--text);font-size:1rem;white-space:pre-line;line-height:1.4"></div>' +
            '<label style="display:flex;gap:12px;align-items:center;font-size:1rem;cursor:pointer;padding:10px 12px;' +
            'border:1px solid var(--border);border-radius:10px;color:var(--text)">' +
            '<input type="checkbox" id="dlg-cb-bifa" style="width:26px;height:26px;flex:none"><span id="dlg-cb-bifa-text"></span></label>' +
            '<div style="display:flex;gap:10px;justify-content:flex-end;flex-wrap:wrap">' +
            '<button class="dx-btn-primary" id="dlg-cb-cancel" style="background:var(--card-bg);border:1px solid var(--border);color:var(--text-muted);padding:12px 22px;min-height:48px">Renunț</button>' +
            `<button class="dx-btn-primary" id="dlg-cb-ok" disabled style="padding:12px 26px;min-height:48px;opacity:.45">${okText}</button>` +
            '</div></div>');
        overlay.querySelector('#dlg-cb-title')!.textContent = title;
        overlay.querySelector('#dlg-cb-body')!.textContent = body;
        overlay.querySelector('#dlg-cb-bifa-text')!.textContent = bifa;
        const bifaEl = overlay.querySelector('#dlg-cb-bifa') as HTMLInputElement;
        const ok = overlay.querySelector('#dlg-cb-ok') as HTMLButtonElement;
        bifaEl.checked = false;
        bifaEl.addEventListener('change', () => { ok.disabled = !bifaEl.checked; ok.style.opacity = bifaEl.checked ? '1' : '.45'; });
        const done = (v: boolean) => { overlay.remove(); document.removeEventListener('keydown', onKey); resolve(v); };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') done(false); };
        ok.addEventListener('click', () => { if (bifaEl.checked) done(true); });
        overlay.querySelector('#dlg-cb-cancel')!.addEventListener('click', () => done(false));
        overlay.querySelector('#dlg-cb-x')!.addEventListener('click', () => done(false));
        overlay.addEventListener('click', e => { if (e.target === overlay) done(false); });
        document.addEventListener('keydown', onKey);
    });
}
